'use client';
import { useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useAssetFinanceCalculators } from '@/lib/hooks/useAssetFinanceCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputField,
  OutputGraphic,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

type FinanceLeaseFormValues = {
  assetPrice: number;
  residualValue: number;
  annualRate: number;
  loanTermYears: number;
  repaymentFrequency: 'monthly' | 'fortnightly' | 'weekly';
  businessUsePercent: number;
};

const FinanceLease = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateFinanceLease } = useAssetFinanceCalculators();

  const methods = useForm<FinanceLeaseFormValues>({
    defaultValues: {
      assetPrice: 50000,
      residualValue: 10000,
      annualRate: 7.5,
      loanTermYears: 5,
      repaymentFrequency: 'monthly',
      businessUsePercent: 100,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateFinanceLease({
    assetPrice: values.assetPrice || 0,
    residualValue: values.residualValue || 0,
    annualRate: values.annualRate || 0,
    loanTermYears: values.loanTermYears || 0,
    repaymentFrequency: values.repaymentFrequency || 'monthly',
    businessUsePercent: values.businessUsePercent ?? 100,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="FinanceLease"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Select lease terms'}>
          <Controller
            name="assetPrice"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Asset Price"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="residualValue"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Residual Value"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Annual Interest Rate"
                suffix="%"
                value={field.value}
                defaultMax={30}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="loanTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Lease Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={10}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="repaymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Payment Frequency"
                value={field.value}
                onChange={(value) =>
                  field.onChange(value as FinanceLeaseFormValues['repaymentFrequency'])
                }
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'fortnightly', label: 'Fortnightly' },
                  { value: 'weekly', label: 'Weekly' },
                ]}
              />
            )}
          />
          <Controller
            name="businessUsePercent"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Business Use"
                suffix="%"
                value={field.value}
                maxValue={100}
                onChange={field.onChange}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Lease Summary'}>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Asset Price',
                  value: values.assetPrice || 0,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Finance Cost',
                  value: result.financeCostRaw,
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Finance Cost" value={`$${result.financeCost}`} />
              <OutputField label="Total Payments" value={`$${result.totalPayments}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField
              label={`${result.paymentLabel} Payment`}
              value={`$${result.leasePayment}`}
            />
            <OutputField label="Residual Value" value={`$${result.residualValue}`} />
          </Styled2ColGrid>
          <OutputField label="Annual Tax-Deductible Amount" value={`$${result.taxDeductPerYear}`} />
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default FinanceLease;
